import type {
  BillingClass, ChangeEstimate, ChangeStage, Channel, CountryCode, ReqPhase, HoldReason, Holiday, Level, Priority, ResolutionCode, SlaCommitment, SlaCommitmentRules, SlaPolicy, SlaView, TicketStatus, TicketType, TransitionOption
} from '@veta-desk/domain';

// Cliente HTTP mínimo. El token vive en localStorage del origen soporte.ascendix.com.mx
// (igual que Veta en el suyo). Toda lectura de storage va en try/catch.

const TOKEN_KEY = 'vetaDeskToken';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token: string) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* sin storage: la sesión dura la pestaña */ }
}
export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* nada que limpiar */ }
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public body: Record<string, unknown> = {}) {
    super(message);
  }
}

const SESSION_CODES = new Set(['SESSION_EXPIRED', 'SESSION_REVOKED']);
let onSessionLost: (message: string) => void = () => {};
export const setSessionLostHandler = (fn: (message: string) => void) => { onSessionLost = fn; };

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers
      }
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'No hay conexión con el servidor.');
  }

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const code = String(body.code ?? 'ERROR');
    const message = String(body.error ?? 'Algo salió mal. Inténtalo de nuevo.');
    if (res.status === 401 && SESSION_CODES.has(code)) {
      clearToken();
      onSessionLost(message);
    }
    throw new ApiError(res.status, code, message, body);
  }
  return body as T;
}

export interface Me {
  uid: string;
  name: string;
  email: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  /** Logo de la empresa para el encabezado (lo sube el superadmin en Veta Horas). */
  tenantLogo?: string | null;
  deskRole: 'agent' | 'team_lead' | 'admin';
  deskRoleLabel: string;
  platformAdmin: boolean;
  serviceManager?: boolean;
  modules?: { desk: boolean; change: boolean };
}

// ---------- Tickets ----------

export interface TicketSummary {
  number: string;
  type: TicketType;
  status: TicketStatus;
  holdReason: HoldReason | null;
  priority: Priority;
  subject: string;
  organizationName: string;
  initiativeCode: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  groupId: string | null;
  groupName: string | null;
  channel?: Channel;
  needsTriage?: boolean;
  requesterName: string;
  estimateHours: number | null;
  updatedAt: string | null;
  createdAt: string | null;
  sla: SlaView;
  response: SlaView;
  risk: 'breached' | 'warning' | 'ok';
  dueInMs: number | null;
  slaCommitment?: SlaCommitment;
}

export type ViewId = 'open' | 'mine' | 'unassigned' | 'myGroup' | 'atRisk' | 'toEstimate' | 'toQuote' | 'awaitingApproval' | 'waitingCustomer' | 'waitingThirdParty' | 'recentlyResolved';

export interface TicketList {
  /** Correos que no se pudieron asignar solos. */
  mailTriage?: number;
  tickets: TicketSummary[];
  counts: Record<ViewId, number>;
  search?: { q: string; total: number };
}

export interface AttachmentView {
  id: string;
  messageId: string | null;
  fileName: string | null; // null si es sensible y el agente no puede verlo
  contentType: string | null;
  size: number;
  sensitive: boolean;
  locked: boolean;
  purged: boolean;
  purgeAfter: string | null;
  uploadedBy: Person;
  canMarkSensitive: boolean;
  createdAt: string | null;
}

export interface Message {
  id: string;
  /** reply = al cliente · note = interna · inbound = lo que escribió el cliente por correo */
  kind: 'reply' | 'note' | 'inbound';
  body: string;
  author: Person;
  attachmentIds: string[];
  fromEmail?: string | null;
  delivery?: { status: 'sent' | 'failed' | 'skipped'; to: string[]; at: string | null; error: string | null } | null;
  createdAt: string | null;
}

export interface Thread {
  messages: Message[];
  attachments: AttachmentView[];
  attachmentsEnabled: boolean;
  mail?: { connected: boolean; to: string | null };
}

export interface MailStatus {
  available: boolean;
  mailbox: {
    address: string; status: 'connected' | 'error'; lastError: string | null; lastPollAt: string | null;
    connectedBy: Person; connectedAt: string | null;
    notify: { ack: boolean; reply: boolean; resolved: boolean; agent: boolean; sla?: boolean };
  } | null;
  organizations: { id: string; name: string; domains: string[] }[];
  activity?: { at: string | null; from: string; subject: string; outcome: string; ticketNumber: string | null; error: string | null }[];
}

export interface TriageItem {
  id: string;
  reason: 'unknown_org' | 'sender_mismatch' | 'closed_ticket';
  from: string;
  fromName: string;
  subject: string;
  text: string;
  receivedAt: string | null;
  ticketNumber: string | null;
  attachments: { fileName: string; size: number }[];
}

async function rawFetch(path: string, init: RequestInit): Promise<Response> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...init.headers } });
  } catch {
    throw new ApiError(0, 'NETWORK', 'No hay conexión con el servidor.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(res.status, String(body.code ?? 'ERROR'), String(body.error ?? 'Algo salió mal. Inténtalo de nuevo.'), body);
  }
  return res;
}

/** Sube un archivo como binario; el nombre, tipo y marca de sensible van en el query. */
export async function uploadAttachment(number: string, file: File, sensitive: boolean): Promise<AttachmentView> {
  const q = new URLSearchParams({ name: file.name, type: file.type || 'application/octet-stream', sensitive: sensitive ? '1' : '0' });
  const res = await rawFetch(`/tickets/${number}/attachments?${q}`, {
    method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' }
  });
  return res.json() as Promise<AttachmentView>;
}

/** Descarga con el token (no hay ligas públicas) y la entrega al navegador. */
export async function downloadAttachment(number: string, a: AttachmentView) {
  const res = await rawFetch(`/tickets/${number}/attachments/${a.id}`, { method: 'GET' });
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = a.fileName ?? 'archivo';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface Person { id: string; name: string }

export interface Ticket {
  number: string;
  type: TicketType;
  status: TicketStatus;
  open: boolean;
  holdReason: HoldReason | null;
  holdNote?: string | null;
  hoursRelease?: { reason: string; by: Person; at: string; until: string } | null;
  resumeTo: TicketStatus | null;
  impact: Level;
  urgency: Level;
  priority: Priority;
  priorityOverride: { value: Priority; reason: string; by: Person; at: string } | null;
  organizationId: string;
  organizationName: string;
  initiativeId: string | null;
  initiativeCode: string | null;
  initiativeName: string | null;
  requester: { name: string; email: string };
  channel: Channel;
  assigneeId: string | null;
  assigneeName: string | null;
  groupId: string | null;
  groupName?: string | null;
  firstResponseAt?: string | null;
  needsTriage?: boolean;
  category: { id: string; label: string } | null;
  subject: string;
  description: string;
  estimate: {
    hours: number; notes: string; version: number; status: 'submitted' | 'approved' | 'rejected';
    submittedAt: string; submittedBy: Person; approvedBy?: string | null; approvalEvidence?: string | null; decidedAt?: string | null;
  } | null;
  /** Solo llega al administrador. */
  quote?: QuoteData | null;
  supportModel?: string | null;
  resolution: { code: ResolutionCode; notes: string; at: string; by: Person } | null;
  closeReason: string | null;
  reopenCount: number;
  createdBy: Person;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteData {
  version: number; estimateHours: number; riskPct: number; mgmtPct: number; marginPct: number;
  billing: 'bag' | 'additional'; subtotalHours: number; mgmtHours: number; hours: number; rate: number | null; unitPrice: number | null; price: number | null; currency: string;
  clientNotes: string; sentAt: string; sentBy: Person; validUntil: string;
  decision: { outcome: 'approved' | 'rejected' | 'changes'; by: string; at: string; reason: string | null } | null;
}

export interface QuoteDraft {
  estimate: { hours: number; notes: string; by: Person } | null;
  sizing: { bagMaxHours: number; reqMaxHours: number };
  factors: { riskPct: number; mgmtPct: number; marginPct: number };
  rate: number | null;
  rateSource: 'overage' | 'sale' | null;
  currency: string;
  supportModel: string | null;
  projectSized: boolean;
}

export interface PortalUserView {
  id: string; email: string; name: string; organizationId: string; role: 'requester' | 'approver'; locale: 'es' | 'en';
  active: boolean; source: 'invited' | 'domain'; lastLoginAt: string | null;
}

export interface PortalOverview {
  portalUrl: string;
  organizations: { id: string; name: string; domains: string[]; users: PortalUserView[] }[];
}

export interface AuditEntry {
  action: string;
  changes: { field: string; before: unknown; after: unknown }[];
  reason: string | null;
  actor: Person;
  at: string | null;
}

export interface TicketDetail {
  ticket: Ticket;
  sla: SlaView;
  response: SlaView;
  slaCalendar: { country: CountryCode; policy: SlaPolicy; commitment: SlaCommitment };
  transitions: TransitionOption[];
  quoteExpired?: boolean;
  audit: AuditEntry[];
}

export interface Meta {
  prefix: string;
  sizing: { bagMaxHours: number; reqMaxHours: number };
  categories: { id: string; service: string; module: string }[];
  organizations: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  agents: { uid: string; name: string; groupIds: string[] }[];
}

export interface AdminConfig {
  tenant: { name: string; slug: string; demo: boolean };
  groups: { id: string; name: string; active: boolean }[];
  categories: { id: string; service: string; module: string; active: boolean }[];
  agents: {
    uid: string; name: string; email: string; deskRole: string; deskRoleLabel: string;
    vetaAdmin: boolean; teamLead: boolean; serviceManager?: boolean; groupIds: string[];
  }[];
  history: { action: string; changes: { field: string; before: unknown; after: unknown }[]; actor: Person; at: string | null }[];
}

export interface InitiativeOption {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  kind: 'project' | 'support' | 'evolutive';
  supportModel: string | null;
}

export interface SlaSettingsData {
  policy: SlaPolicy;
  standard: SlaPolicy;
  commitment: SlaCommitmentRules;
  businessHours: { startHour: number; endHour: number };
  country: CountryCode;
  orgCountries: Record<string, CountryCode>;
  extraHolidays: Partial<Record<CountryCode, Holiday[]>>;
  organizations: { id: string; name: string }[];
  countries: { code: CountryCode; name: string; timezone: string; upcoming: Holiday[] }[];
}

// ---------- Veta Change ----------

export interface ChangeRow {
  number: string; subject: string; organizationName: string; assigneeId: string | null; assigneeName: string | null;
  groupName: string | null; stage: ChangeStage; estimateHours: number | null; estimateVersion: number; updatedAt: string | null;
}

export interface ChangeDetail {
  ticket: {
    number: string; subject: string; description: string; status: TicketStatus; organizationName: string;
    assigneeId: string | null; assigneeName: string | null; initiativeCode: string | null; supportModel: string | null; requesterName: string;
    holdReason?: string | null;
  };
  stage: ChangeStage;
  requiresAbap?: boolean;
  acceptanceCriteria?: string[];
  delivery?: ChangeDelivery | null;
  attachments?: { id: string; fileName: string; size: number; sensitive: boolean; docType: 'functional_spec' | 'technical_spec' | null; createdAt: string | null }[];
  draft: ChangeEstimate;
  totals: { functional: number; abap: number; total: number };
  preliminaryClass: BillingClass;
  versions: { version: number; consultantTotal: number; functionalTotal: number; abapTotal: number; preliminaryClass: BillingClass; submittedBy: Person; submittedAt: string | null }[];
  phases: ReqPhase[];
  sizing: { bagMaxHours: number; reqMaxHours: number };
  permissions: { estimate: boolean; manage: boolean; quote?: boolean; deliver?: boolean };
  adjustRequest?: { reason: string | null; by: string; at: string | null } | null;
  quote?: {
    version: number; riskPct: number; managementPct: number; marginPct: number; rate: number | null; currency: string;
    lines: { phase: string; phaseName: string; discipline: 'functional' | 'abap'; hours: number }[];
    subtotalHours: number; managementHours: number; totalHours: number; unitPrice: number | null; price: number | null;
    billing: 'bag' | 'additional'; clientNotes: string; sentAt: string | null; validUntil: string | null; expired: boolean;
    decision: { outcome: 'approved' | 'rejected' | 'changes'; by: string; at: string | null; reason: string | null } | null;
  } | null;
  evolutive?: {
    status: 'proposed' | 'confirmed';
    proposal: {
      code: string; name: string; parentId: string | null; parentCode: string | null; maxHours: number; saleRate: number; currency: string;
      startDate: string | null; endDate: string | null; assignedUserIds: string[]; assignedNames: string[];
    };
    proposedAt: string | null; confirmedAt: string | null; confirmedBy?: Person; projectId?: string; movedEntries?: number; movedHours?: number;
    vetaConnected: boolean;
    parentOptions: { id: string; code: string; name: string }[];
  } | null;
}

export interface UatRoundView {
  round: number; notes: string; items: { criterion: string; result: 'pending' | 'accepted' | 'rejected'; comment: string }[];
  sentAt: string | null; sentBy: Person; reminderSentAt: string | null; decidedAt: string | null; decidedBy: string | null;
  via: 'portal' | 'agent' | 'auto' | null; evidence: string | null; outcome: 'accepted' | 'rejected' | null; autoAcceptAt: string | null;
}

export interface ChangeDelivery {
  commitment: {
    businessDays: number; pct: number; elapsedMs: number; remainingMs: number; dueAt: string | null; running: boolean;
    pausedReason: 'uat' | 'customer' | null; health: 'ok' | 'warning' | 'late' | 'paused' | 'met' | 'missed';
    startedAt: string | null; stoppedAt: string | null;
  } | null;
  uat: UatRoundView | null;
  uatHistory: UatRoundView[];
  uatSettings: { reminderDay: number; autoAcceptDays: number };
  production: { date: string; transports: string[]; notes: string; warrantyDays: number; warrantyUntil: string; by: Person; at: string | null; inWarranty: boolean } | null;
  closing: { notes: string; functionalDocId: string; technicalDocId: string | null; by: Person; at: string | null } | null;
}

export interface ChangeQuoteDraft {
  stage: ChangeStage;
  estimate: { version: number; lines: ChangeEstimate['lines']; requiresAbap: boolean; consultantTotal: number; preliminaryClass: BillingClass };
  phases: ReqPhase[];
  roundingStep: number;
  managementPctRange: [number, number];
  sizing: { bagMaxHours: number; reqMaxHours: number };
  factors: { riskPct: number; managementPct: number; marginPct: number };
  rate: number | null;
  rateSource: 'overage' | 'sale' | null;
  currency: string;
  clientNotes: string;
}

// ---------- Etapa 2 · contratos ----------

export interface MonthChargeView { month: string; charged: number; actual: number; reqAdjustment: number }
export interface BaselineMonthView {
  month: string; index: number; state: 'closed' | 'current'; base: number; carryIn: number; advanceIn: number; available: number;
  used: number; balance: number; carryOut: number; inBand: number; outOfBand: number;
}
export interface BagView { maxHours: number; used: number; remaining: number; pct: number; level: 'ok' | 'alert' | 'over' }
export interface BaselineSummary {
  current: BaselineMonthView | null; level: 'ok' | 'alert' | 'band' | 'out' | 'not_started' | 'ended';
  poolRemaining: number | null; projectedExhaustMonth: string | null; endMonth?: string;
  terms?: { monthlyHours: number; startMonth: string; months: number; rolloverPct: number; flexBandPct: number; bandSource: 'pool' | 'next_month' };
}
export interface ContractProject {
  id: string; code: string; name: string; clientId: string | null; clientName: string | null; active: boolean; startDate: string | null; endDate: string | null;
}
export interface ContractRow {
  project: ContractProject; model: 'bag' | 'baseline' | 'on_demand'; month: string; thisMonth: MonthChargeView;
  bag: BagView | null; baseline: BaselineSummary | null;
}
export interface ContractDetail extends Omit<ContractRow, 'baseline'> {
  months: MonthChargeView[];
  reqs: { number: string; subject: string; status: string; quotedHours: number; actualHours: number; closedMonth: string | null }[];
  baseline: (BaselineSummary & { rows: BaselineMonthView[]; totalInBand: number; totalOutOfBand: number; lastMonthAdvance: number; endMonth: string }) | null;
}
export interface TicketContract {
  contract: (Omit<ContractRow, 'baseline'> & { baseline: BaselineSummary | null; consumesContract: boolean; canOpen: boolean }) | null;
  quoteVsActual: { actual: number; quoted: number; pct: number; over: boolean; billing: 'bag' | 'additional' } | null;
}

// ---------- Etapa 2.3 · cierre mensual ----------

export interface StatementView {
  projectId: string; projectCode: string; projectName: string; organizationId: string | null; organizationName: string;
  model: 'bag' | 'baseline' | 'on_demand'; month: string; status: 'draft' | 'sent' | 'in_clarification' | 'closed';
  sentAt: string | null; sentBy: Person | null; dueAt: string | null; closedAt: string | null; closedVia: 'client' | 'auto' | 'agent' | null; acceptedBy: string | null;
  clarifications: { id: string; ticket: string | null; comment: string; by: string; at: string | null; resolvedAt: string | null; resolvedBy: Person | null; resolution: string | null }[];
  openClarifications: number;
}
export interface StatementDetail {
  statement: StatementView;
  summary: {
    month: string; model: string; charged: number; actual: number; adjustmentsHours: number;
    bag: BagView | null;
    baseline: { row: BaselineMonthView | null; poolRemaining: number | null; projectedExhaustMonth: string | null } | null;
    adjustments: { id?: string; kind: string; hours: number; ticket: string | null; reason: string; warrantyOf: string | null; approvedBy: Person; at: string | null }[];
  };
  lines: { ticket: string; subject: string; type: string | null; actual: number; charged: number; completion: number }[];
  permissions: { send: boolean; adjust: boolean; resolve: boolean; close: boolean };
}

// ---------- documentos del contrato ----------

export interface ContractDocs {
  enabled: boolean; canUpload: boolean; canDelete: boolean;
  documents: { id: string; fileName: string; size: number; uploadedBy: Person; createdAt: string | null }[];
}

export async function uploadContractDoc(projectId: string, file: File): Promise<ContractDocs> {
  const q = new URLSearchParams({ name: file.name, type: file.type || 'application/octet-stream' });
  const res = await rawFetch(`/contracts/${projectId}/documents?${q}`, { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } });
  return res.json() as Promise<ContractDocs>;
}

export async function downloadContractDoc(projectId: string, doc: { id: string; fileName: string }) {
  const res = await rawFetch(`/contracts/${projectId}/documents/${doc.id}`, { method: 'GET' });
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
